import {
  applySnapshot,
  getSnapshot,
  Instance,
  SnapshotIn,
  types,
} from 'mobx-state-tree';
import defaultLocales from 'globals/locales/default';
import { LoadingState, LoadingStates } from 'globals/types';
import { temporaryEndpoints } from 'http/endpoints';
import { isStoreInitialized, performSetAction } from 'models/utils/utils';
import { LeaderSingleRowDataProps } from 'components/UserPanel/Pages/Dashboard/Leaderboard/LeaderList/LeaderSingleRow/LeaderSingleRow';
import { SingleUser, SingleUserProps } from './single-user/single-user';

export interface LeaderboardListProps {
  data: SingleUserProps[];
  status: LoadingState;
}

export const LeaderboardList = types
  .model({
    data: types.optional(types.array(SingleUser), []),
    status: types.optional(
      types.enumeration(LoadingStates),
      LoadingState.INITIAL
    ),
  })
  .actions((self) => ({
    set(props: LeaderboardListProps): void {
      applySnapshot(self, {
        ...getSnapshot(self),
        ...props,
      });
    },
    updateStatus(status: LoadingState): void {
      applySnapshot(self, {
        ...getSnapshot(self),
        status,
      });
    },
  }))
  .actions((self) => ({
    async setLeaderboard(): Promise<void> {
      await performSetAction({
        store: self,
        endpoint: temporaryEndpoints.leaderboardList,
      });
    },
  }))
  .actions((self) => ({
    async init(): Promise<void> {
      const { status } = getSnapshot(self);
      if (isStoreInitialized(status)) return;
      await self.setLeaderboard();
    },
    async update(): Promise<void> {
      await self.setLeaderboard();
    },
  }))
  .views((self) => ({
    getRecentLeaderboardListItems(size = 5): LeaderSingleRowDataProps[] {
      const { data } = self;
      return getSnapshot(data)
        .slice(0, size)
        .map(({ id, name, invitations, reports }) => ({
          id,
          name,
          markers: [
            { label: defaultLocales.invitations, value: invitations },
            { label: defaultLocales.reports, value: reports },
          ],
        }));
    },
    getLeaderboardListItems(): SingleUserProps[] {
      const { data } = self;
      return getSnapshot(data);
    },
  }));

export const leaderboardList = LeaderboardList.create();
export type LeaderboardListSnapshot = SnapshotIn<typeof LeaderboardList>;
export type LeaderboardListInstance = Instance<typeof LeaderboardList>;
export default leaderboardList;
